import {
  type AssetClassBranch,
  type Branch,
  Strategy,
} from '@interfaces/financialProduct';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Box } from '@ovComponents/1-primative/box/box';
import { Grid } from '@ovComponents/1-primative/grid/grid';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Badge } from '@ovComponents/2-component/badge/badge';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { AssetClassSelect } from '@ovComponents/3-pattern/assetClassSelect/assetClassSelect';
import { NumberField } from '@ovComponents/3-pattern/numberField/numberField';
import {
  DEFAULT_MODEL_PORTFOLIO_GROUP_BY,
  MODEL_PORTFOLIO_GROUP_BY,
  type ModelPortfolioGroupBy,
} from '@ovComponents/4-module/workflowCompletion/subSteps/createModelPortfolio/modelPortfolioGroupBy';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { mapValues, round } from 'lodash-es';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { AssetClassTargetAllocation } from '../assetClassTargetAllocation.types';

export const AssetClassesTargetAllocations = ({
  targetAllocations,
  setTargetAllocations,
  isFreeForm = false,
  securities = [],
  testId = 'asset-classes-target-allocations',
  isEditPage = false,
  modelId,
  groupBy = DEFAULT_MODEL_PORTFOLIO_GROUP_BY,
  strategy,
  assetClassBranches,
}: {
  targetAllocations: AssetClassTargetAllocation[];
  setTargetAllocations: (
    targetAllocations: AssetClassTargetAllocation[],
  ) => void;
  isFreeForm?: boolean;
  securities?: Branch[];
  testId?: string;
  isEditPage?: boolean;
  modelId?: string;
  groupBy?: ModelPortfolioGroupBy;
  strategy?: Strategy;
  assetClassBranches?: AssetClassBranch[];
}) => {
  const { t } = useTranslation(['modelPortfolios', 'buildModels']);
  const { sendAnalytic } = useContext(AnalyticsContext);

  const actualPercentages = useMemo(() => {
    if (strategy === Strategy.ASSET_CLASS) {
      const fromBranches: Record<string, number> = {};

      for (const branch of assetClassBranches ?? []) {
        const id = branch.assetClass?.id;
        const tp = branch.targetPercentage;

        if (id != null && tp != null) {
          fromBranches[id] = round(tp, 2);
        }
      }

      return fromBranches;
    }

    const allAllocations = securities.reduce(
      (acc: { [key: string]: number }, security: Branch) => {
        const financialProduct = security.financialProduct;

        const assetClassId =
          groupBy === MODEL_PORTFOLIO_GROUP_BY.SECONDARY
            ? financialProduct?.secondaryAssetClass?.id
            : financialProduct?.primaryAssetClass?.id;

        if (!assetClassId) {
          return acc;
        }

        const percentage = security.percentage || 0;
        acc[assetClassId] = (acc[assetClassId] || 0) + percentage;

        return acc;
      },
      {},
    );

    return mapValues(allAllocations, (x: number) => round(x, 2));
  }, [securities, groupBy, strategy, assetClassBranches]);

  const handleAssetClassChange = (id: string) => (event: any) => {
    const selectedValue = event.target.value;
    // Only update if the selected value is a valid asset class ID
    if (selectedValue && selectedValue !== '') {
      setTargetAllocations(
        targetAllocations.map((allocation) =>
          allocation.secondaryAssetClass.id === id
            ? { ...allocation, secondaryAssetClass: { id: selectedValue } }
            : allocation,
        ),
      );
    }
  };

  const handlePercentageChange =
    (id: string, field: 'min' | 'max' | 'actualPercentage') =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      // Only allow numbers and empty string
      if (
        value === '' ||
        (/^\d*\.?\d*$/.test(value) && Number.parseFloat(value) <= 100)
      ) {
        setTargetAllocations(
          targetAllocations.map((allocation) =>
            allocation.secondaryAssetClass.id === id
              ? {
                  ...allocation,
                  [field]: value === '' ? 0 : Number.parseFloat(value),
                }
              : allocation,
          ),
        );
      }
    };

  const addAllocation = () => {
    const eventName = isEditPage
      ? ovAnalyticsEvents.modelPageEditTabInvestmentPolicyStatementAddAssetClass
      : ovAnalyticsEvents.modelPageCreateStep4AddIpsAddIps;
    sendAnalytic(eventName, {
      ...(isEditPage && modelId ? { modelId } : {}),
      currentAllocationsCount: targetAllocations.length,
      timestamp: new Date().toISOString(),
    });
    const newAllocation: AssetClassTargetAllocation = {
      secondaryAssetClass: {
        id: String(Math.floor(1000000000 + Math.random() * 9000000000)),
      },
      min: 0,
      max: 0,
      actualPercentage: 0,
      color: 'supportOne',
      fromSecurity: false,
    };
    setTargetAllocations([...targetAllocations, newAllocation]);
  };

  const removeAllocation = (id: string) => {
    setTargetAllocations(
      targetAllocations.filter(
        (allocation) => allocation.secondaryAssetClass.id !== id,
      ),
    );
  };

  const usedAssetClasses = targetAllocations
    .map((allocation) => allocation.secondaryAssetClass.id)
    .filter((id): id is string => Boolean(id));

  return (
    <Box
      sx={{
        border: isFreeForm ? 'none' : '1px solid',
        borderColor: 'divider',
        borderRadius: isFreeForm ? 0 : 1,
        pl: isFreeForm ? 0 : 2,
        pt: isFreeForm ? 0 : 2,
        pb: isFreeForm ? 0 : 1,
      }}
    >
      {targetAllocations.map((allocation, index) => {
        const assetClassId = allocation.secondaryAssetClass?.id || '';
        const actualPercentage = actualPercentages[assetClassId] || 0;
        const isInBounds =
          actualPercentage >= allocation.min &&
          actualPercentage <= allocation.max;

        return (
          <Grid
            container
            spacing={1.5}
            key={allocation.secondaryAssetClass.id}
            sx={{
              mb: 2,
              ml: isFreeForm ? -1 : 1.5,
            }}
          >
            <Grid
              item
              sx={{
                width: isFreeForm ? 'auto' : '160px',
                flex: isFreeForm ? 1 : 'none',
                minWidth: '160px',
              }}
            >
              <AssetClassSelect
                testId={`${testId}-asset-class-select-${index}`}
                value={allocation.secondaryAssetClass.id || ''}
                setAssetClass={handleAssetClassChange(
                  allocation.secondaryAssetClass.id || '',
                )}
                label={t('assetClass')}
                filteredAllocations={usedAssetClasses}
                omitAny={true}
                required
              />
            </Grid>
            <Grid item sx={{ width: '110px', flex: 'none' }}>
              <NumberField
                testId={`${testId}-min-input-${index}`}
                label={t('min')}
                number={allocation.min.toString()}
                setNumber={(value) =>
                  handlePercentageChange(
                    allocation.secondaryAssetClass.id || '',
                    'min',
                  )({
                    target: { value },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
                required
                error={allocation.min < 0 || allocation.min > 100}
                trailingIcon='percent'
                unsigned
              />
            </Grid>
            <Grid item sx={{ width: '110px', flex: 'none' }}>
              <NumberField
                testId={`${testId}-max-input-${index}`}
                label={t('max')}
                number={allocation.max.toString()}
                setNumber={(value) =>
                  handlePercentageChange(
                    allocation.secondaryAssetClass.id || '',
                    'max',
                  )({
                    target: { value },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
                required
                error={
                  allocation.max < 0 ||
                  allocation.max > 100 ||
                  allocation.max < allocation.min
                }
                trailingIcon='percent'
                unsigned
              />
            </Grid>
            <Grid item sx={{ width: '80px', flex: 'none' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography
                  data-testid={`${testId}-actual-percentage-${index}`}
                  variant='labelSmall'
                  sx={{ mb: '4px', color: 'text.secondary' }}
                >
                  {t('buildModels:actual')}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mt: '7px',
                  }}
                >
                  <Badge
                    label={`${actualPercentage.toFixed(2)}%`}
                    color={isInBounds ? 'positive' : 'negative'}
                    data-testid={`${testId}-actual-percentage-badge-${index}`}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '14px',
                        fontFamily: 'Interphases Pro',
                        fontWeight: 400,
                        borderRadius: '4px',
                      },
                    }}
                  />
                </Box>
              </Box>
            </Grid>
            {targetAllocations.length > 1 && (
              <Grid
                item
                sx={{
                  width: '40px',
                  flex: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  mt: 1.5,
                  ml: isFreeForm ? -2 : 0,
                }}
              >
                <IconButton
                  data-testid={`${testId}-remove-allocation-button-${index}`}
                  onClick={() =>
                    removeAllocation(allocation.secondaryAssetClass.id || '')
                  }
                  size='small'
                  sx={{
                    color: 'GrayText',
                    padding: '4px',
                  }}
                  aria-label='delete asset class allocation'
                >
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </Grid>
            )}
          </Grid>
        );
      })}
      <Typography
        data-testid={`${testId}-add-asset-class-branch-button`}
        sx={{
          fontSize: '12px',
          fontWeight: 500,
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.8,
          },
          color: 'primary.main',
          pb: isFreeForm ? 0 : 2,
          display: 'inline-block',
        }}
        onClick={addAllocation}
      >
        {t('addAssetClassBranch')}
      </Typography>
    </Box>
  );
};
